import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  ArrowRight, 
  Shield, 
  Heart, 
  TrendingUp, 
  Wrench,
  Users,
  Building2,
  GraduationCap,
  Target,
  ChevronRight,
  Phone,
  MessageCircle,
  Megaphone,
  Download,
  FileText
} from 'lucide-react';
import { FadeIn, StaggerChildren } from '@/components/Animations';
import AnimatedCounter from '@/components/AnimatedCounter';

const MLM_TYPOGRAPHY = "https://customer-assets.emergentagent.com/job_5389659f-dea1-4240-af6c-b14886122f6e/artifacts/822wkfab_Striking%20Typography%20for%20%23MyLifeMatters.png";
const GIGFLEET_LOGO = "https://customer-assets.emergentagent.com/job_5389659f-dea1-4240-af6c-b14886122f6e/artifacts/plrdoiq9_Gig%20Fleet%20Logo.png";
const BIKE_IMAGE = "https://customer-assets.emergentagent.com/job_csi-partnership-site/artifacts/qgewfg06_ChatGPT%20Image%20Mar%2014%2C%202026%2C%2008_18_28%20AM.png";
const GIGFLEET_APP_IMAGE = "https://customer-assets.emergentagent.com/job_csi-partnership-site/artifacts/ui0koxj2_image.png";
const RIDER_IMAGE = "https://customer-assets.emergentagent.com/job_csi-partnership-site/artifacts/tf4bj6a1_ChatGPT%20Image%20Mar%2014%2C%202026%2C%2008_51_51%20AM.png";
const CORPORATE_IMAGE = "https://images.unsplash.com/photo-1758519288905-38b7b00c1023?auto=format&fit=crop&q=80&w=800";

const pillars = [
  {
    icon: Heart,
    title: 'WeCare',
    description: 'Rider wellbeing, support systems, accountability, daily engagement, and safety culture.',
    color: 'bg-rose-50 text-rose-600',
    borderColor: 'border-rose-200',
  },
  {
    icon: TrendingUp,
    title: 'Thrive',
    description: 'Entrepreneurial development, training, assignments, personal growth, and business discipline.',
    color: 'bg-emerald-50 text-emerald-600',
    borderColor: 'border-emerald-200',
  },
  {
    icon: Wrench,
    title: 'Tools',
    description: 'Gig Fleet App, AI-driven assignments, training modules, operational reporting, and business tools.',
    color: 'bg-blue-50 text-blue-600',
    borderColor: 'border-blue-200',
  },
];

const impactStats = [
  { label: 'Riders Supported', icon: Users },
  { label: 'Partners', icon: Building2 },
  { label: 'Training Hours', icon: GraduationCap },
  { label: 'Safety Campaigns', icon: Shield },
];

const HomePage = () => {
  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-gradient-to-br from-slate-50 via-purple-50/30 to-white">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -right-1/4 w-[600px] h-[600px] bg-purple-100/40 rounded-full blur-3xl" />
          <div className="absolute -bottom-1/4 -left-1/4 w-[500px] h-[500px] bg-yellow-100/30 rounded-full blur-3xl" />
        </div>

        <div className="container-wide relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <FadeIn delay={0.2}>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-heading font-bold text-slate-900 leading-[0.95]">
                  <span className="text-primary-mlm">#</span>MyLife<span className="text-primary-mlm">Matters</span>
                </h1>
              </FadeIn>

              <FadeIn delay={0.3}>
                <p className="text-xl md:text-2xl text-slate-600 leading-relaxed max-w-lg">
                  Creating a New Gig Economy and Social Compact for Gigpreneurs.
                </p>
              </FadeIn>

              <FadeIn delay={0.4}>
                <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                  <Button
                    asChild
                    size="lg"
                    className="bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full px-6 sm:px-8 py-5 sm:py-6 font-semibold shadow-lg hover:shadow-xl transition-all text-sm sm:text-base"
                    data-testid="hero-partner-btn"
                  >
                    <Link to="/corporate-csi">
                      Partner With Us
                      <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-full px-6 sm:px-8 py-5 sm:py-6 font-semibold transition-all text-sm sm:text-base"
                    data-testid="hero-whatsapp-btn"
                  >
                    <a href="https://wa.me/27720286584" target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="mr-2 w-4 sm:w-5 h-4 sm:h-5" />
                      Chat on WhatsApp
                    </a>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="ghost"
                    className="text-slate-700 hover:text-primary-mlm hover:bg-purple-50 rounded-full px-6 sm:px-8 py-5 sm:py-6 font-semibold transition-all text-sm sm:text-base"
                    data-testid="hero-contact-btn"
                  >
                    <Link to="/contact">
                      <Phone className="mr-2 w-4 sm:w-5 h-4 sm:h-5" />
                      Contact Us
                    </Link>
                  </Button>
                </div>
              </FadeIn>
            </div>

            {/* Right Image */}
            <FadeIn delay={0.3} direction="left">
              <div className="relative mt-8 lg:mt-0">
                <div className="absolute inset-0 bg-gradient-to-br from-primary-mlm/20 to-yellow-400/20 rounded-2xl sm:rounded-3xl transform rotate-2 sm:rotate-3 scale-105 hidden sm:block" />
                <img
                  src={BIKE_IMAGE}
                  alt="MLM Branded Delivery Bike"
                  className="relative rounded-2xl sm:rounded-3xl shadow-2xl w-full object-cover max-h-[400px] sm:max-h-[500px] lg:max-h-none"
                  data-testid="hero-bike-image"
                />
                {/* Floating badge */}
                <div className="absolute -bottom-3 -left-2 sm:-bottom-4 sm:-left-4 bg-white rounded-xl sm:rounded-2xl shadow-xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-safety-yellow rounded-full flex items-center justify-center">
                    <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-slate-900" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-semibold text-slate-900">Safety First</p>
                    <p className="text-xs text-slate-500">Every rider matters</p>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-16 md:py-24 lg:py-32 bg-white" data-testid="why-matters-section">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-center">
            <FadeIn direction="right">
              <div className="relative order-2 lg:order-1">
                <img
                  src={RIDER_IMAGE}
                  alt="Delivery Rider"
                  className="rounded-2xl sm:rounded-3xl shadow-xl w-full object-cover aspect-[4/3]"
                />
              </div>
            </FadeIn>

            <FadeIn delay={0.2}>
              <div className="space-y-5 sm:space-y-6 order-1 lg:order-2">
                <span className="inline-block px-4 py-2 bg-purple-100 text-primary-mlm rounded-full text-sm font-semibold tracking-wide uppercase">
                  Why This Matters
                </span>
                <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900">
                  The Reality of Delivery Riders
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Delivery bike riders are the backbone of South Africa's growing gig economy. 
                  Yet they work as independent contractors without safety nets, structured support, 
                  or a clear path to economic stability.
                </p>
                <div className="space-y-4 pt-4">
                  {[
                    'Rider safety concerns on dangerous roads',
                    'Lack of medical benefits or insurance',
                    'No structured training or support',
                    'No clear pathway to ownership or growth',
                  ].map((issue, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="w-2 h-2 bg-rose-500 rounded-full" />
                      </div>
                      <p className="text-slate-700">{issue}</p>
                    </div>
                  ))}
                </div>
                <Button
                  asChild
                  variant="link"
                  className="text-primary-mlm hover:text-[#5b21b6] p-0 font-semibold text-base"
                  data-testid="learn-problem-btn"
                >
                  <Link to="/problem">
                    Learn more about the problem
                    <ChevronRight className="ml-1 w-5 h-5" />
                  </Link>
                </Button>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* The Movement */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="movement-section">
        <div className="container-wide">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-4 py-2 bg-purple-100 text-primary-mlm rounded-full text-sm font-semibold tracking-wide uppercase mb-6">
                The Movement
              </span>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 mb-6">
                #MyLifeMatters Movement
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                We're building awareness around the lives, safety, and economic dignity of delivery 
                riders. The movement promotes a new social compact where riders receive structure, 
                tools, and support to build sustainable businesses.
              </p>
            </div>
          </FadeIn>

          {/* Gigpreneur Development Model Image */}
          <FadeIn delay={0.1}>
            <div className="flex justify-center mb-12 md:mb-16 px-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_csi-partnership-site/artifacts/g2hvg7hr_image.png"
                alt="The Gigpreneur Development Model"
                className="max-w-full md:max-w-3xl lg:max-w-4xl w-full rounded-xl sm:rounded-2xl shadow-lg"
              />
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8">
            {['Awareness', 'Safety', 'Opportunity'].map((pillar, index) => (
              <FadeIn key={pillar} delay={0.1 * index}>
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    {index === 0 && <Megaphone className="w-8 h-8 text-primary-mlm" />}
                    {index === 1 && <Shield className="w-8 h-8 text-primary-mlm" />}
                    {index === 2 && <TrendingUp className="w-8 h-8 text-primary-mlm" />}
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">{pillar}</h3>
                  <p className="text-slate-600">
                    {index === 0 && 'Creating public awareness about rider safety and the importance of their work in our economy.'}
                    {index === 1 && 'Promoting road safety culture, proper training, and protective gear for all riders.'}
                    {index === 2 && 'Building pathways for riders to grow from survival mode to sustainable entrepreneurship.'}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Powered by Gig Fleet */}
      <section className="py-16 md:py-24 lg:py-32 bg-white" data-testid="gigfleet-section">
        <div className="container-wide">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 lg:p-16 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-primary-mlm/10 rounded-full blur-3xl" />
            
            <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center relative z-10">
              <FadeIn>
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <span className="text-slate-400 text-xs sm:text-sm font-medium uppercase tracking-wider">Powered By</span>
                    <img src={GIGFLEET_LOGO} alt="Gig Fleet Management" className="h-6 sm:h-8 w-auto" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-white">
                    Technology-Enabled Rider Empowerment
                  </h2>
                  <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
                    Gig Fleet Management provides the operational infrastructure behind #MyLifeMatters. 
                    Through technology, training, and structured systems, we're transforming how 
                    riders work and grow.
                  </p>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 pt-2 sm:pt-4">
                    {['Technology Platform', 'Training Programs', 'Operational Systems', 'Growth Pathway'].map((item) => (
                      <div key={item} className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-safety-yellow rounded-full flex-shrink-0" />
                        <span className="text-white text-xs sm:text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    asChild
                    className="bg-safety-yellow hover:bg-yellow-400 text-slate-900 rounded-full px-6 sm:px-8 py-5 sm:py-6 font-semibold shadow-lg hover:shadow-xl transition-all mt-2 sm:mt-4 text-sm sm:text-base"
                    data-testid="learn-solution-btn"
                  >
                    <Link to="/solution">
                      Explore Our Solution
                      <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
                    </Link>
                  </Button>
                </div>
              </FadeIn>

              <FadeIn delay={0.2} direction="left">
                <div className="relative mt-6 lg:mt-0">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/20">
                    <img
                      src={GIGFLEET_APP_IMAGE}
                      alt="Gig Fleet Mobile App"
                      className="rounded-lg sm:rounded-xl w-full object-cover"
                    />
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* How The Model Works - Three Pillars */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="pillars-section">
        <div className="container-wide">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-4 py-2 bg-purple-100 text-primary-mlm rounded-full text-sm font-semibold tracking-wide uppercase mb-6">
                How The Model Works
              </span>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 mb-6">
                Three Pillars of Success
              </h2>
              <p className="text-lg text-slate-600">
                Our integrated approach combines care, growth, and technology to create 
                sustainable success for every rider.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => (
              <FadeIn key={pillar.title} delay={0.1 * index}>
                <div className={`bg-white rounded-2xl p-8 border-2 ${pillar.borderColor} hover:shadow-xl hover:-translate-y-2 transition-all duration-300 h-full`}>
                  <div className={`w-16 h-16 ${pillar.color} rounded-2xl flex items-center justify-center mb-6`}>
                    <pillar.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-slate-900 mb-4">{pillar.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{pillar.description}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Corporate CSI Opportunity */}
      <section className="py-20 md:py-32 bg-white" data-testid="csi-section">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <FadeIn delay={0.1} direction="right">
              <div className="relative">
                <img
                  src={CORPORATE_IMAGE}
                  alt="Corporate Partnership"
                  className="rounded-3xl shadow-xl w-full object-cover aspect-[4/3]"
                />
                <div className="absolute -top-6 -right-6 bg-primary-mlm rounded-2xl p-6 shadow-xl text-white">
                  <Target className="w-8 h-8 mb-2" />
                  <p className="text-sm font-semibold">CSI Impact</p>
                </div>
              </div>
            </FadeIn>

            <FadeIn>
              <div className="space-y-6">
                <span className="inline-block px-4 py-2 bg-purple-100 text-primary-mlm rounded-full text-sm font-semibold tracking-wide uppercase">
                  Corporate CSI
                </span>
                <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900">
                  Partner With Purpose
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Join leading corporates in supporting rider safety and economic empowerment. 
                  Your CSI investment creates measurable social impact while providing valuable 
                  brand visibility across urban areas.
                </p>
                <div className="space-y-4 pt-4">
                  {[
                    'Meaningful social impact investment',
                    'Direct support for rider safety initiatives',
                    'Brand visibility on delivery bikes',
                    'Quarterly impact reporting',
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-slate-700">{benefit}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-4 pt-4">
                  <Button
                    asChild
                    className="bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full px-8 py-6 font-semibold shadow-lg hover:shadow-xl transition-all"
                    data-testid="become-partner-btn"
                  >
                    <Link to="/corporate-csi">
                      Become a Partner
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </Button>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Download Partnership Brochure */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900">
        <div className="container-wide">
          <FadeIn>
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 max-w-4xl mx-auto">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center shrink-0">
                  <FileText className="w-7 h-7 text-purple-300" />
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-heading font-bold text-white mb-2">
                    Download Our Partnership Brochure
                  </h3>
                  <p className="text-purple-200 text-sm md:text-base">
                    Get the full details on how your organisation can partner with #MyLifeMatters 
                    to create measurable social impact through rider safety and empowerment.
                  </p>
                </div>
              </div>
              <a
                href="/MyLifeMatters_Call_For_Partnership.pdf"
                download
                className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-slate-900 rounded-full px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-all whitespace-nowrap"
                data-testid="home-download-pdf-btn"
              >
                <Download className="w-5 h-5" />
                Download PDF
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Advertising With Purpose */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-purple-900 via-purple-800 to-slate-900" data-testid="advertising-section">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <FadeIn>
              <div className="space-y-6">
                <span className="inline-block px-4 py-2 bg-white/10 text-safety-yellow rounded-full text-sm font-semibold tracking-wide uppercase">
                  Advertising With Purpose
                </span>
                <h2 className="text-4xl md:text-5xl font-heading font-bold text-white">
                  Your Brand, Moving Through Cities
                </h2>
                <p className="text-lg text-purple-200 leading-relaxed">
                  Delivery bikes travel thousands of kilometers daily through high-traffic urban areas. 
                  Turn every delivery into brand exposure while supporting rider safety.
                </p>
                <div className="grid grid-cols-2 gap-6 pt-4">
                  {[
                    { label: 'Daily km covered' },
                    { label: 'Daily impressions' },
                    { label: 'Urban coverage' },
                    { label: 'Visibility' },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                      <p className="text-sm text-purple-200">{stat.label}</p>
                    </div>
                  ))}
                </div>
                <Button
                  asChild
                  className="bg-safety-yellow hover:bg-yellow-400 text-slate-900 rounded-full px-8 py-6 font-semibold shadow-lg hover:shadow-xl transition-all mt-4"
                  data-testid="explore-advertising-btn"
                >
                  <Link to="/advertising">
                    Explore Advertising
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} direction="left">
              <div className="relative">
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-4 border border-white/20">
                  <img
                    src={BIKE_IMAGE}
                    alt="Branded Delivery Bike"
                    className="rounded-2xl w-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white rounded-2xl shadow-xl px-6 py-4 flex items-center gap-4">
                  <Megaphone className="w-8 h-8 text-primary-mlm" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Moving Billboards</p>
                    <p className="text-xs text-slate-500">Across South Africa</p>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-20 md:py-32 bg-white" data-testid="impact-stats-section">
        <div className="container-wide">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-4 py-2 bg-purple-100 text-primary-mlm rounded-full text-sm font-semibold tracking-wide uppercase mb-6">
                Our Impact
              </span>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 mb-6">
                Making a Real Difference
              </h2>
            </div>
          </FadeIn>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {impactStats.map((stat, index) => (
              <FadeIn key={stat.label} delay={0.1 * index}>
                <div className={`rounded-2xl p-8 text-center ${index === 0 ? 'bg-safety-yellow' : 'bg-slate-50'}`}>
                  <stat.icon className={`w-10 h-10 mx-auto mb-4 ${index === 0 ? 'text-slate-900' : 'text-primary-mlm'}`} />
                  <p className={`text-sm font-medium ${index === 0 ? 'text-slate-700' : 'text-slate-600'}`}>
                    {stat.label}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.4}>
            <div className="text-center mt-12">
              <Button
                asChild
                variant="outline"
                className="border-2 border-primary-mlm text-primary-mlm hover:bg-primary-mlm hover:text-white rounded-full px-8 py-6 font-semibold transition-all"
                data-testid="view-impact-btn"
              >
                <Link to="/impact">
                  View Full Impact Report
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="cta-section">
        <div className="container-wide">
          <FadeIn>
            <div className="bg-slate-900 rounded-3xl p-8 md:p-16 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
              
              <div className="relative z-10 max-w-3xl mx-auto space-y-6">
                <h2 className="text-4xl md:text-5xl font-heading font-bold text-white">
                  Join #MyLifeMatters
                </h2>
                <p className="text-xl text-slate-300 leading-relaxed">
                  Whether you're a corporate looking to make an impact, a rider seeking opportunity, 
                  or someone who believes in our cause — there's a place for you in this movement.
                </p>
                <div className="flex flex-wrap justify-center gap-4 pt-6">
                  <Button
                    asChild
                    size="lg"
                    className="bg-white text-primary-mlm hover:bg-slate-100 rounded-full px-8 py-6 font-semibold shadow-lg hover:shadow-xl transition-all"
                    data-testid="cta-partner-btn"
                  >
                    <Link to="/corporate-csi">
                      Partner With Us
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    className="bg-safety-yellow text-slate-900 hover:bg-yellow-400 rounded-full px-8 py-6 font-semibold shadow-lg hover:shadow-xl transition-all"
                    data-testid="cta-rider-btn"
                  >
                    <Link to="/for-riders">
                      I'm a Rider
                    </Link>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-primary-mlm rounded-full px-8 py-6 font-semibold transition-all"
                    data-testid="cta-whatsapp-btn"
                  >
                    <a href="https://wa.me/27720286584" target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="mr-2 w-5 h-5" />
                      Talk on WhatsApp
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
